import { useState, useEffect, useCallback, useContext } from "react"
import {
    colorCode,
    consoleLog,
    riskProfile,
    safetyPercentage,
} from "../utilities/helpers"
import {
    getReviewsSummary,
    getNewsSummary,
} from "../services/chaperonConnector"
import VicinityContext from "../context/vicinityContext"
import TextBlockHolder from "../components/TextBlockHolder"
import { SESSIONSTORAGE_ENTITY } from "../const/storage"
import { NEWS_REFRESH, REVIEWS_REFRESH } from "../const/numbers"

export default function PlaceSummary({ placeId }) {
    const [reviewSummary, setReviewSummary] = useState([])
    const [newsSummary, setNewsSummary] = useState([])

    consoleLog("PlaceSummary component loaded for placeId:", placeId)

    let location = useContext(VicinityContext).location
    let countryCode = useContext(VicinityContext).countryCode

    const loadSummary = useCallback(async () => {
        let placeData = JSON.parse(
            sessionStorage.getItem(`${SESSIONSTORAGE_ENTITY}-${placeId}`)
        )

        if (
            !placeData?.news?.summary?.risk_summary ||
            (placeData?.news?.summary?.summary_date &&
                new Date() - new Date(placeData.news.summary.summary_date) >
                    NEWS_REFRESH)
        ) {
            placeData = {
                ...placeData,
                news: {
                    summary: await getNewsSummary(location, countryCode),
                },
            }
            sessionStorage.setItem(
                `${SESSIONSTORAGE_ENTITY}-${placeId}`,
                JSON.stringify(placeData)
            )
            consoleLog(`Cached news summary data for ${placeId}.`)
        } else {
            consoleLog(`Using cached news summary data for ${placeId}.`)
        }
        const newsSummaryData = placeData.news.summary
        setNewsSummary(newsSummaryData)

        if (
            !placeData?.reviews?.summary?.risk_summary ||
            (placeData?.reviews?.summary?.summary_date &&
                new Date() - new Date(placeData.reviews.summary.summary_date) >
                    REVIEWS_REFRESH)
        ) {
            placeData = {
                ...placeData,
                reviews: {
                    summary: await getReviewsSummary(placeId),
                },
            }
            sessionStorage.setItem(
                `${SESSIONSTORAGE_ENTITY}-${placeId}`,
                JSON.stringify(placeData)
            )
            consoleLog(`Cached reviews summary data for ${placeId}.`)
        } else {
            consoleLog(`Using cached reviews summary data for ${placeId}.`)
        }
        const reviewSummaryData = placeData.reviews.summary
        setReviewSummary(reviewSummaryData)
    }, [placeId, location, countryCode])

    useEffect(() => {
        loadSummary()
    }, [loadSummary])

    return (
        <>
            {reviewSummary?.risk_score ? (
                <>
                    <h3 className="featurette-heading fw-normal lh-1 pt-2 align-items-left text-start">
                        What others are{" "}
                        <span className="text-body-secondary">saying...</span>
                    </h3>
                    <span className="text-start text-justify d-block lead">
                        {reviewSummary?.risk_summary}{" "}
                        <kbd
                            className={`fs-6 bg-${colorCode(
                                safetyPercentage(reviewSummary?.risk_score)
                            )}`}
                        >
                            {riskProfile(
                                safetyPercentage(reviewSummary?.risk_score)
                            )}
                        </kbd>
                    </span>
                    <br />
                </>
            ) : null}

            {newsSummary?.risk_score ? (
                <>
                    <h3 className="featurette-heading fw-normal lh-1 pt-2 align-items-right">
                        ...and what's been in the{" "}
                        <span className="text-body-secondary">news</span>
                    </h3>
                    <span className="text-start text-justify d-block lead">
                        {newsSummary?.risk_summary}{" "}
                        <kbd
                            className={`fs-6 bg-${colorCode(
                                safetyPercentage(newsSummary?.risk_score)
                            )}`}
                        >
                            {riskProfile(
                                safetyPercentage(newsSummary?.risk_score)
                            )}
                        </kbd>
                    </span>
                </>
            ) : null}

            {!reviewSummary?.risk_score && !newsSummary?.risk_score && (
                <TextBlockHolder />
            )}
        </>
    )
}
